import React from "react";
import {
  Button,
  Col,
  Flex,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Tooltip,
  Typography,
  Upload,
} from "antd";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import RequiredLabel from "../../../../common/required-label/RequiredLabel";
import { PaperClipOutlined } from "@ant-design/icons";
import styles from "./TermsAndConditions.module.scss";
import { fileDocumentUploadAction } from "../../../../../redux/services/auth/authServices";
import { useDispatch } from "react-redux";
import { InfoButtonIcon } from "../../../../../assets/svg";
import { colors } from "../../../../../constants/colors";
import { checkIfFileTypeIsValidForPdfAttachment, readImageFileAsync } from "../../../../../utils/utils";
import { createTermsAndConditions } from "../../../../../redux/services/terms-and-conditions/termsAndConditionsApis";
import { createTermsAndConditionsAction } from "../../../../../redux/services/terms-and-conditions/termsAndConditionsServices";
import { toastService } from "../../../../common/toast-messages/toast";
import { userRoles } from "../../../../../constants";

const TermsAndConditions = () => {
  /* i18n dependencies */
  const { t } = useTranslation();
  /* i18n dependencies */

  /* dispatch dependencies */
  const dispatch = useDispatch();
  /* dispatch dependencies */

  /* module dependencies for attachment - multiple */
  const [openPreviewDialog, setOpenPreviewDialog] = useState(false);
  const [previewImageSrc, setPreviewImageSrc] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");
  const [multipleAttachmentList, setMultipleAttachmentList] = useState([]);
  /* module dependencies for attachment - multiple */

  /* open image preview for dialog - multiple attachment */
  const handleAttachmentPreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await readImageFileAsync(file.originFileObj);
    }
    setPreviewImageSrc(file?.url || file?.preview);
    setOpenPreviewDialog(true);
    const tempPreviewTitle =
      file?.name || file?.url?.substring(file?.url?.lastIndexOf("/") + 1);
    setPreviewTitle(tempPreviewTitle);
  };
  /* open image preview for dialog - multiple attachment */

  /* loading dependencies */
  const [loading, setLoading] = useState(false);
  const [selectListLoader, setSelectListLoader] = useState(false);
  /* loading dependencies */

  /* form dependencies */
  const [form] = Form.useForm();
  /* form dependencies */

  const onSubmit = (values) => {
    if (
      !multipleAttachmentList ||
      multipleAttachmentList === undefined ||
      multipleAttachmentList === null ||
      multipleAttachmentList.length === 0
    ) {
      toastService.error(t("CommonUtils.pleaseUploadAPDF"));
      return;
    }

    const dataToBeSentForPayload = {
      url: multipleAttachmentList?.map(
        (element) => element?.url?.documentUrl
      )[0],
      name: multipleAttachmentList?.map((element) => element?.name)[0],
    };
    const dataToBeSent = {
      type: values?.type,
    };

    dispatch(
      createTermsAndConditionsAction(dataToBeSent, dataToBeSentForPayload)
    ).then(() => {
      form.resetFields();
      setMultipleAttachmentList([]);
    });
  };

  return (
    <div className={styles.termsAndConditionsContainer}>
      <Flex vertical gap="2rem">
        <Typography.Title level={2}>
          {t("AppModuleNames.SuperAdmin.TermsAndCondition")}
        </Typography.Title>
        <Form
          form={form}
          layout="vertical"
          autoComplete="off"
          requiredMark={false}
          style={{ maxWidth: "400px" }}
          onFinish={onSubmit}
        >
          <Form.Item
            name="type"
            label={
              <RequiredLabel
                text={t("Modules.Configuration.userType")}
                required={true}
              />
            }
            rules={[
              {
                required: true,
                message: t("Modules.Configuration.pleaseSelectUserType"),
              },
            ]}
          >
            <Select
              loading={selectListLoader}
              options={[
                { value: userRoles.CUSTOMER, label: "User" },
                {
                  value: userRoles.SERVICE_PROVIDER_ADMIN,
                  label: "Service Provider",
                },
              ]}
              placeholder={t("Modules.Configuration.selectUserType")}
            />
          </Form.Item>

          <Form.Item>
            <Flex vertical gap="1rem">
              <Typography.Title
                level={4}
                style={{ display: "flex", alignItems: "center", gap: "0.3rem" }}
              >
                {t("Modules.TermsAndConditions.upload")}
                <span style={{ color: "red" }}>*</span>
                <Tooltip
                  placement="rightTop"
                  title={t("Modules.TermsAndConditions.uploadAPdfTooltipInfo")}
                  color={colors.DarkMint}
                >
                  <Button type="text" shape="circle" size="small">
                    <Flex align="center" justify="center">
                      <InfoButtonIcon />
                    </Flex>
                  </Button>
                </Tooltip>
              </Typography.Title>

              <Flex vertical align="center" justify="center">
                <Upload
                  listType="picture-card"
                  accept="application/pdf"
                  fileList={multipleAttachmentList}
                  onPreview={handleAttachmentPreview}
                  onChange={(options) => {

                    const attachmentFileName = options.file.name;
                    const fileTypeIsValidRes = checkIfFileTypeIsValidForPdfAttachment(attachmentFileName);
    
                    if (fileTypeIsValidRes.isValid === false) {
                      toastService.error(fileTypeIsValidRes.isInvalidMessage);
                    } else {
                      setMultipleAttachmentList(options.fileList);
                    }
                  }}
                  onRemove={(file) =>
                    setMultipleAttachmentList(
                      multipleAttachmentList?.filter(
                        (element) => element.uid !== file.uid
                      )
                    )
                  }

                  customRequest={(options) => {
                    const attachmentFileName = options.file.name;
                    const fileTypeIsValidRes = checkIfFileTypeIsValidForPdfAttachment(attachmentFileName);
    
                    if (fileTypeIsValidRes.isValid === false) {
                      toastService.error(fileTypeIsValidRes.isInvalidMessage);
                    } else {
                      const formData = new FormData();
                    formData.append("document", options.file);
                    dispatch(fileDocumentUploadAction(formData)).then(
                      (apiRes) => {
                        const documentUrl = apiRes?.[0];
                        options.file["url"] = documentUrl;
                        options.onSuccess(documentUrl, options.file);
                      }
                    );
                    }
                  }}
                  
                  className="uploadContainer"
                  maxCount={1}
                >
                  <Button
                    type="primary"
                    shape="circle"
                    size="middle"
                    icon={<PaperClipOutlined />}
                  />
                </Upload>
                <Modal
                  open={openPreviewDialog}
                  title={previewTitle}
                  footer={null}
                  onCancel={() => setOpenPreviewDialog(false)}
                >
                  <img style={{ width: "100%" }} src={previewImageSrc} />
                </Modal>
              </Flex>
            </Flex>
          </Form.Item>

          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            disabled={loading}
            style={{ marginTop: "10px" }}
          >
            {t("CommonUtils.submit")}
          </Button>
        </Form>
      </Flex>
    </div>
  );
};

export default TermsAndConditions;
